import { getApiBaseUrl } from '../config/constants';
import { AuthenticationError, extractAuthError } from '../utils/authError';
import { t } from '../i18n/translate';

// Get API base URL - centralized configuration
const API_BASE_URL = getApiBaseUrl();

export interface InventoryItem {
  [key: string]: any;
}

export interface InventoryUsageMeta {
  entry_count: number;
  complete_count?: number;
  planned_count?: number;
  amends_completed_count?: number;
  has_plan?: boolean;
  plan_word_count?: number;
  practiced_3_days?: boolean;
}

export interface SaveInventoryRequest {
  inventory_type: string;
  version: string;
  items: InventoryItem[];
  encryptedData?: string;
  encrypted?: boolean;
  entry_count?: number;
  usage_meta?: InventoryUsageMeta;
}

export interface DeleteInventoryRequest {
  inventory_type: string;
  version: string;
}

export interface FeedbackSubmission {
  userEmail: string;
  locationInApp: string;
  feedbackType: 'General' | 'Error / Bug' | 'Idea' | 'Language / Translation' | '2026 Workshop Question';
  feedbackText: string;
  deviceInfo: {
    deviceType: string;
    browser: string;
    browserVersion: string;
    operatingSystem: string;
    screenResolution: string;
    userAgent: string;
  };
  emailCopyToUser: boolean;
  /** Language the app was showing when the feedback was sent. */
  language?: string;
}

export interface ShareDocument {
  id: string;
  user_id: string;
  share_name: string;
  document_type: string;
  generation_date: string;
  share_url: string;
  expire_date: string;
  created_by_user_id?: string;
  created_by_name?: string;
  program?: string;
}

export interface CreateShareDocumentRequest {
  share_name: string;
  document_type: string;
  html_content: string;
  on_behalf_of_user_id?: string;
  program?: string;
}

export interface UserKey {
  userId: string;
  encryptedKey: string;
  createdAt: string;
  lastUsed: string;
}

export interface SaveUserKeyRequest {
  userId: string;
  encryptedKey: string;
  createdAt: string;
  lastUsed: string;
}

// Sponsorship Types
export interface SponsorshipInvitation {
  id: string;
  inviterUserId: string;
  inviteeEmail: string;
  inviteeDisplayName?: string;
  program: string;
  role: 'sponsor' | 'sponsee';
  status: 'pending' | 'accepted' | 'declined' | 'canceled' | 'expired';
  token: string;
  tokenExpiresAt: string;
  createdAt: string;
  updatedAt: string;
  acceptedAt?: string;
  declinedAt?: string;
  canceledAt?: string;
}

export interface SponsorshipRelationship {
  id: string;
  sponseeUserId: string;
  sponsorUserId?: string;
  sponsorEmail: string;
  sponsorDisplayName?: string;
  program: string;
  status: 'active' | 'ended';
  createdAt: string;
  updatedAt: string;
  endedAt?: string;
  endedBy?: string;
  endedReason?: string;
}


class ApiService {
  private getAuthToken(): string | null {
    // Use custom authentication token
    return localStorage.getItem('auth_token');
  }

  private async makePublicRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = this.getAuthToken();
    if (!token) {
      throw new AuthenticationError(t('auth.error.no_token'));
    }

    const url = `${API_BASE_URL}${endpoint}`;
    
    // Don't set Content-Type for FormData (browser will set it with boundary)
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string> || {}),
    };
    
    // Only set Content-Type to JSON if body is not FormData
    if (!(options.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check for authentication errors
    if (response.status === 401) {
      const errorText = await response.text();
      throw new AuthenticationError(extractAuthError(response, errorText));
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  async listInventoryVersions(inventoryType: string): Promise<string[]> {
    const response = await this.makeRequest(`/list_inventory_versions/${inventoryType}`);
    return response.versions || [];
  }

  async getInventory(inventoryType: string, version: string): Promise<InventoryItem[]> {
    const encodedVersion = encodeURIComponent(version);
    const response = await this.makeRequest(`/get_inventory/${inventoryType}/${encodedVersion}`);
    return response.items || [];
  }

  async getInventoryEncrypted(inventoryType: string, version: string): Promise<{ items: InventoryItem[]; encrypted?: boolean; encryptedData?: string }> {
    const encodedVersion = encodeURIComponent(version);
    const response = await this.makeRequest(`/get_inventory_encrypted/${inventoryType}/${encodedVersion}`);
    return response;
  }

  async saveInventory(inventoryType: string, version: string, items: InventoryItem[]): Promise<void> {
    const request: SaveInventoryRequest = {
      inventory_type: inventoryType,
      version,
      items,
    };

    await this.makeRequest('/save_inventory', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async saveInventoryEncrypted(request: SaveInventoryRequest): Promise<void> {
    await this.makeRequest('/save_inventory_encrypted', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async deleteInventory(inventoryType: string, version: string): Promise<void> {
    const request: DeleteInventoryRequest = {
      inventory_type: inventoryType,
      version,
    };

    await this.makeRequest('/delete_inventory', {
      method: 'DELETE',
      body: JSON.stringify(request),
    });
  }

  async getInventoryCompletion(inventoryType: string, version: string): Promise<{ completed: boolean }> {
    // Since there's no dedicated completion endpoint, we'll store completion status
    // as a special item in the inventory data itself
    try {
      const items = await this.getInventory(inventoryType, version);
      // Look for a special completion item
      const completionItem = items.find(item => item._type === 'completion_status');
      return { completed: completionItem?.completed || false };
    } catch (error) {
      console.error('Error getting completion status:', error);
      return { completed: false };
    }
  }

  async saveInventoryCompletion(inventoryType: string, version: string, completed: boolean): Promise<void> {
    // Since there's no dedicated completion endpoint, we'll store completion status
    // as a special item in the inventory data itself
    try {
      const items = await this.getInventory(inventoryType, version);
      
      // Remove any existing completion item
      const filteredItems = items.filter(item => item._type !== 'completion_status');
      
      // Add the completion status as a special item
      const completionItem = {
        _type: 'completion_status',
        completed,
        timestamp: new Date().toISOString()
      };
      
      const updatedItems = [...filteredItems, completionItem];
      
      // Save the updated inventory data
      await this.saveInventory(inventoryType, version, updatedItems);
    } catch (error) {
      console.error('Error saving completion status:', error);
      throw error;
    }
  }

  async submitFeedback(feedback: FeedbackSubmission): Promise<void> {
    await this.makeRequest('/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  }

  async whoami(): Promise<{ email: string }> {
    return this.makeRequest('/whoami');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

        getLoginUrl(): string {
    // Custom authentication - no external login URL needed
    return window.location.origin;
  }

  async createShareDocument(request: CreateShareDocumentRequest): Promise<{ document_id: string; share_url: string }> {
    return this.makeRequest('/share/documents', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async listShareDocuments(): Promise<ShareDocument[]> {
    const response = await this.makeRequest('/share/documents');
    return response.documents || [];
  }

  async listSponseeSnapshots(sponseeUserId: string, program: string): Promise<ShareDocument[]> {
    const response = await this.makeRequest(`/share/documents?for_user_id=${encodeURIComponent(sponseeUserId)}&program=${encodeURIComponent(program)}`);
    return response.documents || [];
  }

  async createSnapshotForSponsee(sponseeUserId: string, request: CreateShareDocumentRequest): Promise<{ document_id: string; share_url: string }> {
    return this.makeRequest('/share/documents', {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        on_behalf_of_user_id: sponseeUserId
      }),
    });
  }

  async deleteShareDocument(documentId: string): Promise<void> {
    await this.makeRequest('/share/documents', {
      method: 'DELETE',
      body: JSON.stringify({ document_id: documentId })
    });
  }

  async getShareRedirect(documentId: string): Promise<{ share_url: string; share_name: string; expire_date: string }> {
    return this.makePublicRequest(`/share/redirect/${documentId}`);
  }

  async sendSnapshotEmail(request: {
    document_id: string;
    recipient_email: string;
    recipient_name?: string;
  }): Promise<void> {
    await this.makeRequest('/share/send-email', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async listProgressArchives(program = 'SAA', track = '12-steps'): Promise<any> {
    const response = await this.makeRequest(
      `/progress/archives?program=${encodeURIComponent(program)}&track=${encodeURIComponent(track)}`
    );
    const archives = Array.isArray(response?.archives) ? response.archives : [];
    return {
      archives,
      count: typeof response?.count === 'number' ? response.count : archives.length,
      max: typeof response?.max === 'number' ? response.max : 5,
      max_with_auto: typeof response?.max_with_auto === 'number' ? response.max_with_auto : 6,
    };
  }

  async createProgressArchive(body: {
    archive_name: string;
    clear_active?: boolean;
    is_auto_archive?: boolean;
    program?: string;
    track?: string;
  }): Promise<any> {
    return this.makeRequest('/progress/archives', {
      method: 'POST',
      body: JSON.stringify({
        program: 'SAA',
        track: '12-steps',
        clear_active: false,
        is_auto_archive: false,
        ...body,
      }),
    });
  }

  async getProgressArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<any> {
    return this.makeRequest(
      `/progress/archives/${encodeURIComponent(archiveId)}?program=${encodeURIComponent(program)}&track=${encodeURIComponent(track)}`
    );
  }

  async restoreProgressArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<any> {
    return this.makeRequest(
      `/progress/archives/${encodeURIComponent(archiveId)}/restore?program=${encodeURIComponent(program)}&track=${encodeURIComponent(track)}`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  }

  async deleteProgressArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<void> {
    await this.makeRequest(
      `/progress/archives/${encodeURIComponent(archiveId)}?program=${encodeURIComponent(program)}&track=${encodeURIComponent(track)}`,
      { method: 'DELETE' }
    );
  }

  async adminRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.makeRequest(endpoint, options);
  }

  /** Binary admin download (e.g. usage export zip). */
  async adminDownload(endpoint: string): Promise<Blob> {
    const token = this.getAuthToken();
    if (!token) {
      throw new AuthenticationError(t('auth.error.no_token'));
    }
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        // API Gateway only returns decoded binary when Accept matches BinaryMediaTypes
        Accept: 'application/octet-stream, application/zip',
      },
    });
    if (response.status === 401) {
      throw new AuthenticationError(t('auth.error.session_expired'));
    }
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}`);
    }
    const buffer = await response.arrayBuffer();
    const bytes = this.decodeApiGatewayBinaryPayload(buffer);
    return new Blob([bytes], { type: 'application/zip' });
  }

  /**
   * API Gateway may return base64 text instead of raw bytes when Accept does not
   * match configured binary media types. UEsD… is base64 for ZIP magic PK..
   */
  private decodeApiGatewayBinaryPayload(buffer: ArrayBuffer): Uint8Array {
    const bytes = new Uint8Array(buffer);
    if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
      return bytes;
    }
    if (bytes.length >= 4 && bytes[0] === 0x55 && bytes[1] === 0x45 && bytes[2] === 0x73) {
      const text = new TextDecoder('ascii').decode(bytes).replace(/\s/g, '');
      const binary = atob(text);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
      }
      return out;
    }
    return bytes;
  }

  // User Key Management Methods
  async saveUserKey(request: SaveUserKeyRequest): Promise<void> {
    await this.makeRequest('/user/keys', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getUserKey(userId: string): Promise<UserKey | null> {
    try {
      return await this.makeRequest(`/user/keys/${encodeURIComponent(userId)}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async updateUserKeyLastUsed(userId: string): Promise<void> {
    await this.makeRequest(`/user/keys/${encodeURIComponent(userId)}/last-used`, {
      method: 'PUT',
      body: JSON.stringify({ lastUsed: new Date().toISOString() }),
    });
  }

  async deleteUserKey(userId: string): Promise<void> {
    await this.makeRequest(`/user/keys/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  }

  // Sponsorship API Methods
  async sponsorshipRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.makeRequest(`/sponsorship${endpoint}`, options);
  }

  // Sponsor Management
  async getSponsors(): Promise<any> {
    return this.makeRequest('/sponsors');
  }

  async createSponsor(sponsor: { program: string; name: string; email: string }): Promise<any> {
    return this.makeRequest('/sponsors', {
      method: 'POST',
      body: JSON.stringify(sponsor),
    });
  }

  async deleteSponsor(sponsorId: string): Promise<void> {
    await this.makeRequest(`/sponsors/${sponsorId}`, {
      method: 'DELETE',
    });
  }

  // Sponsee management
  async getSponsees(): Promise<any> {
    return this.makeRequest('/sponsees');
  }

  async createSponsee(sponsee: { program: string; name: string; email: string }): Promise<any> {
    return this.makeRequest('/sponsees', {
      method: 'POST',
      body: JSON.stringify(sponsee),
    });
  }

  async deleteSponsee(sponseeId: string): Promise<void> {
    await this.makeRequest(`/sponsees/${sponseeId}`, {
      method: 'DELETE',
    });
  }

  // Invitation methods
  async sendInvitation(invitation: { to_email: string; relationship_type: string; message?: string; program?: string }): Promise<any> {
    return this.makeRequest('/invitations', {
      method: 'POST',
      body: JSON.stringify(invitation),
    });
  }

  async getPendingInvitations(): Promise<any> {
    return this.makeRequest('/invitations/pending');
  }

  async getSentInvitations(): Promise<any> {
    return this.makeRequest('/invitations/sent');
  }

  async getInvitationByToken(token: string): Promise<any> {
    // This is a public endpoint (no auth required) for non-users clicking invitation links
    const response = await fetch(`${API_BASE_URL}/invitations/token/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async respondToInvitation(invitationId: string, response: 'accept' | 'decline'): Promise<any> {
    return this.makeRequest(`/invitations/${invitationId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ invitation_id: invitationId, response }),
    });
  }

  // Auto-share methods
  async toggleAutoShare(sponsorId: string, enabled: boolean): Promise<any> {
    return this.makeRequest(`/sponsors/${sponsorId}/auto-share`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async getSponseeProgress(shareToken: string): Promise<any> {
    return this.makeRequest(`/sponsee-progress/${shareToken}`);
  }

  // Sponsee data access (for sponsors with auto-share)
  async getSponseeStepProgress(sponseeUserId: string, program: string): Promise<any> {
    return this.makeRequest(`/sponsee-data/${encodeURIComponent(sponseeUserId)}/progress/${encodeURIComponent(program)}`);
  }

  async getSponseeInventories(sponseeUserId: string): Promise<any> {
    return this.makeRequest(`/sponsee-data/${encodeURIComponent(sponseeUserId)}/inventories`);
  }
}

export const apiService = new ApiService(); 